import { Box, Button, CircularProgress, LinearProgress, makeStyles, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CloudIcon from '@material-ui/icons/Cloud';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import SearchIcon from '@material-ui/icons/Search';
import StorageIcon from '@material-ui/icons/Storage';
import Skeleton from '@material-ui/lab/Skeleton';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { ImpactSearchMessages, ImpactSearchState } from '../../models/ImpactSearch/IImpactSearch';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: theme.spacing(5, 2),
        margin: '0 auto',
        maxWidth: 560,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3, 2),
        },
    },
    iconCircle: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#EAF3FB',
        marginBottom: theme.spacing(2),
    },
    icon: {
        fontSize: 38,
        color: '#215176',
    },
    iconError: {
        fontSize: 38,
        color: '#B00020',
    },
    // --- Connection Search animation (WEBVIEW -> IMPACT -> results back) -----
    conSearch: {
        position: 'relative',
        width: 340,
        height: 118,
        flexShrink: 0,
    },
    conNode: {
        position: 'absolute',
        top: 30,
        width: 78,
        height: 66,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
    },
    conNodeLabel: {
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
    },
    webviewNode: {
        left: 0,
        backgroundColor: '#EAF3FB',
        border: '1.5px solid #B9D2E6',
        color: '#215176',
        '& svg': { fontSize: 24, color: '#215176' },
    },
    impactNode: {
        right: 0,
        backgroundColor: '#215176',
        border: '1.5px solid #215176',
        color: 'white',
        zIndex: 1,
        '& svg': { fontSize: 24, color: 'white' },
    },
    impactRing: {
        position: 'absolute',
        top: 63,
        right: 39,
        width: 66,
        height: 66,
        margin: '-33px -33px 0 0',
        borderRadius: 12,
        border: '2px solid #2E6DA0',
        opacity: 0,
        animation: '$impactPing 4.8s ease-out infinite',
    },
    impactRing2: {
        animationDelay: '0.5s',
    },
    conWire: {
        position: 'absolute',
        top: 61,
        left: 82,
        right: 82,
        height: 4,
        backgroundColor: '#DCE7F0',
        borderRadius: 2,
    },
    resultPacket: {
        position: 'absolute',
        top: 59.5,
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#2E7D5B',
        boxShadow: '0 0 6px rgba(46, 125, 91, 0.7)',
        opacity: 0,
        animation: '$impactPacketReturn 4.8s ease-in-out infinite',
    },
    resultPacket2: {
        animationDelay: '0.35s',
    },
    resultPacket3: {
        animationDelay: '0.7s',
    },
    magnifier: {
        position: 'absolute',
        top: 8,
        left: 20,
        zIndex: 2,
        filter: 'drop-shadow(0 2px 4px rgba(33, 81, 118, 0.35))',
        animation: '$impactMagTravel 4.8s ease-in-out infinite',
        '& svg': { fontSize: 34, color: '#2E6DA0' },
    },
    foundTag: {
        position: 'absolute',
        top: 96,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        color: '#2E7D5B',
        opacity: 0,
        animation: '$impactFoundBlink 4.8s ease-in-out infinite',
    },
    '@keyframes impactPing': {
        '0%, 34%': { transform: 'scale(0.9)', opacity: 0 },
        '40%': { transform: 'scale(1)', opacity: 0.5 },
        '62%': { transform: 'scale(1.55)', opacity: 0 },
        '100%': { transform: 'scale(1.55)', opacity: 0 },
    },
    '@keyframes impactPacketReturn': {
        '0%, 48%': { right: 86, opacity: 0 },
        '54%': { opacity: 1 },
        '88%': { right: 'calc(100% - 96px)', opacity: 1 },
        '94%, 100%': { right: 'calc(100% - 96px)', opacity: 0 },
    },
    '@keyframes impactMagTravel': {
        '0%': { left: 18, top: 26, transform: 'rotate(0deg)', opacity: 1 },
        '30%': { left: 246, top: 12, transform: 'rotate(0deg)', opacity: 1 },
        '38%': { left: 238, top: 18, transform: 'rotate(-14deg)' },
        '46%': { left: 258, top: 20, transform: 'rotate(12deg)' },
        '54%': { left: 240, top: 24, transform: 'rotate(-10deg)' },
        '62%': { left: 254, top: 18, transform: 'rotate(8deg)', opacity: 1 },
        '72%': { left: 250, top: 16, transform: 'rotate(0deg)', opacity: 0 },
        '82%': { left: 18, top: 26, opacity: 0 },
        '92%, 100%': { left: 18, top: 26, opacity: 1 },
    },
    '@keyframes impactFoundBlink': {
        '0%, 50%': { opacity: 0 },
        '60%, 86%': { opacity: 1 },
        '95%, 100%': { opacity: 0 },
    },
    '@keyframes impactFadeIn': {
        from: { opacity: 0, transform: 'translateY(4px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    '@media (prefers-reduced-motion: reduce)': {
        magnifier: { animation: 'none' },
        resultPacket: { animation: 'none' },
        impactRing: { animation: 'none', opacity: 0.25 },
        foundTag: { animation: 'none' },
        stageText: { animation: 'none' },
    },
    stageText: {
        color: '#215176',
        fontSize: 14,
        fontWeight: 600,
        minHeight: 22,
        animation: '$impactFadeIn 400ms ease-out',
    },
    elapsedText: {
        color: '#8A96A0',
        fontSize: 12,
        marginTop: 2,
        fontVariantNumeric: 'tabular-nums',
    },
    progressRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        maxWidth: 460,
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(2.5),
    },
    progressBar: {
        flex: 1,
        height: 5,
        borderRadius: 3,
        backgroundColor: '#DCE7F0',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#2E6DA0',
        },
    },
    skeletonBlock: {
        width: '100%',
        maxWidth: 480,
        textAlign: 'left',
    },
    skeletonRow: {
        display: 'flex',
        gap: '10px',
        marginBottom: 8,
        alignItems: 'center',
    },
    // --- shared ---------------------------------------------------------------
    title: {
        fontWeight: 'bold',
        color: '#215176',
        marginBottom: theme.spacing(1),
    },
    description: {
        color: '#5A6B7A',
        fontSize: 13.5,
        marginBottom: theme.spacing(2.5),
        lineHeight: 1.6,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchButton: {
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 'bold',
        textTransform: 'none',
        padding: '6px 20px',
        backgroundColor: '#215176',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1A4160',
        },
        '&.Mui-disabled': {
            backgroundColor: '#B8C6D1',
            color: '#F2F5F8',
        },
    },
    modifyButton: {
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 'bold',
        textTransform: 'none',
        padding: '5px 18px',
        borderColor: '#215176',
        color: '#215176',
    },
    // Compact text action at the end of the progress bar (the standard
    // transfer-with-cancel pattern). Quiet by default; red intent appears
    // only on hover — cancelling a search is safe, not destructive.
    cancelButton: {
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        color: '#6B7A88',
        minWidth: 0,
        padding: '2px 10px',
        borderRadius: 14,
        '& .MuiButton-startIcon': {
            marginRight: 4,
        },
        '&:hover': {
            color: '#B00020',
            backgroundColor: 'rgba(176, 0, 32, 0.05)',
        },
    },
    helperText: {
        color: '#8A96A0',
        fontSize: 12,
        marginTop: theme.spacing(1.5),
    },
}));

// Honest indeterminate progress: the stages advance with elapsed time so the
// user always sees forward motion, without pretending to know a percentage.
const SEARCH_STAGES: { after: number, text: string }[] = [
    { after: 0, text: 'Connecting to the IMPACT system...' },
    { after: 6, text: 'Searching account records in IMPACT...' },
    { after: 20, text: 'Scanning matching accounts — this can take a little longer for large record sets...' },
    { after: 45, text: 'Retrieving and validating the results. Thanks for your patience...' },
    { after: 90, text: 'Almost there — finalizing the search results...' },
];

interface ImpactSearchEmptyStateProps {
    searchText: string;
    canSearchImpact: boolean;
    searchState: ImpactSearchState;
    isCancelling: boolean;
    onSearchImpact: () => void;
    onModifySearch: () => void;
    onCancelSearch: () => void;
}

const ImpactSearchEmptyState: React.FC<ImpactSearchEmptyStateProps> = (props) => {
    const classes = useStyles();
    const { searchText, canSearchImpact, searchState, isCancelling, onSearchImpact, onModifySearch, onCancelSearch } = props;
    const isSearching = searchState === 'searching';
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        if (!isSearching) {
            setElapsedSeconds(0);
            return;
        }
        const startedAt = Date.now();
        const timer = setInterval(() => {
            setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, [isSearching]);

    if (isSearching) {
        const stage = [...SEARCH_STAGES].reverse().find(s => elapsedSeconds >= s.after) ?? SEARCH_STAGES[0];
        return (
            <Box className={classes.root} id="impact-search-empty-state" role="status" aria-live="polite">
                <Box className={classes.conSearch} aria-hidden="true">
                    <span className={classes.impactRing} />
                    <span className={`${classes.impactRing} ${classes.impactRing2}`} />
                    <Box className={`${classes.conNode} ${classes.webviewNode}`}>
                        <StorageIcon />
                        <span className={classes.conNodeLabel}>WEBVIEW</span>
                    </Box>
                    <Box className={classes.conWire} />
                    <span className={classes.resultPacket} />
                    <span className={`${classes.resultPacket} ${classes.resultPacket2}`} />
                    <span className={`${classes.resultPacket} ${classes.resultPacket3}`} />
                    <Box className={`${classes.conNode} ${classes.impactNode}`}>
                        <CloudIcon />
                        <span className={classes.conNodeLabel}>IMPACT</span>
                    </Box>
                    <Box className={classes.magnifier}>
                        <SearchIcon />
                    </Box>
                    <Box className={classes.foundTag}>
                        RECORDS FOUND — RETURNING RESULTS
                    </Box>
                </Box>
                <Typography variant="h6" className={classes.title} style={{ marginTop: 14 }}>
                    Searching the IMPACT System
                </Typography>
                <Typography key={stage.after} className={classes.stageText}>
                    {stage.text}
                </Typography>
                <Typography className={classes.elapsedText}>
                    {elapsedSeconds > 2 ? `Elapsed ${elapsedSeconds}s — please keep this page open` : ' '}
                </Typography>
                <Box className={classes.progressRow}>
                    <LinearProgress className={classes.progressBar} />
                    <Button
                        id="impact-cancel-search-button"
                        size="small"
                        className={classes.cancelButton}
                        disabled={isCancelling}
                        onClick={onCancelSearch}
                        startIcon={isCancelling ? <CircularProgress size={12} color="inherit" /> : <CloseIcon style={{ fontSize: 15 }} />}
                    >
                        {isCancelling ? 'Cancelling...' : 'Cancel'}
                    </Button>
                </Box>
                <Box className={classes.skeletonBlock} aria-hidden="true">
                    {[92, 100, 84].map((width, index) => (
                        <Box key={index} className={classes.skeletonRow} style={{ opacity: 1 - index * 0.25 }}>
                            <Skeleton variant="circle" width={22} height={22} />
                            <Skeleton variant="rect" height={14} style={{ flex: 1, maxWidth: `${width}%`, borderRadius: 4 }} />
                            <Skeleton variant="rect" height={14} width={56} style={{ borderRadius: 4 }} />
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    }

    const getTitle = (): string => {
        if (searchState === 'noRecords') return 'No Records Found in Either System';
        if (searchState === 'error') return 'IMPACT System Search Failed';
        return 'No Records Found';
    };

    const getDescription = (): string => {
        if (searchState === 'noRecords') return ImpactSearchMessages.noRecords;
        if (searchState === 'error') return ImpactSearchMessages.error;
        return ImpactSearchMessages.initial;
    };

    return (
        <Box className={classes.root} id="impact-search-empty-state">
            <Box className={classes.iconCircle}>
                {searchState === 'error' ?
                    <ErrorOutlineIcon className={classes.iconError} />
                    : searchState === 'noRecords' ?
                        <InfoOutlinedIcon className={classes.icon} />
                        : <SearchIcon className={classes.icon} />
                }
            </Box>
            <Typography variant="h6" className={classes.title}>
                {getTitle()}
            </Typography>
            <Typography variant="body2" className={classes.description}>
                {getDescription()}
                {searchText && searchState === 'idle' ? ` Current search: "${searchText}".` : null}
            </Typography>
            <Box className={classes.actions}>
                {searchState === 'idle' || searchState === 'error' ?
                    <Button
                        id="impact-search-button"
                        variant="contained"
                        className={classes.searchButton}
                        disabled={!canSearchImpact}
                        onClick={onSearchImpact}
                        startIcon={<CloudDownloadIcon />}
                    >
                        {searchState === 'error' ? 'Retry IMPACT System Search' : 'Search in IMPACT System'}
                    </Button>
                    : null}
                <Button
                    id="impact-modify-search-button"
                    variant="outlined"
                    className={classes.modifyButton}
                    onClick={onModifySearch}
                >
                    Modify Search Criteria
                </Button>
            </Box>
            {!canSearchImpact ?
                <Typography className={classes.helperText}>
                    {ImpactSearchMessages.selectField}
                </Typography>
                : null}
        </Box>
    );
};

export default ImpactSearchEmptyState;
